import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { checkPermission } from '@shared/lib/checkPermissions';
import permissions from '@shared/config/permissions';
import { ASSESSMENT_STATUSES } from '../model/statuses';

/**
 * The `created` status ("Instant Created") exists only for instant
 * assessments. Accounts without the instant-assessments capability
 * never see it in their data, so the filter option is hidden for
 * them (customer request 2026-08-24). DS accounts check the child
 * mask too — child accounts with instants enabled surface the
 * status in the DS list.
 *
 * Shared by every table whose Status filter offers assessment
 * statuses (assessments table, bot activities table).
 */
export const useCanSeeInstantCreated = () => {
  const {
    isDistributionSponsor,
    metadata: { currentAccountPermissions, childAccountPermissions },
  } = useSelector(state => state.user);

  return useMemo(
    () =>
      checkPermission(currentAccountPermissions, permissions.MANAGE_INSTANT_SURVEY_ASSESSMENTS) ||
      (isDistributionSponsor &&
        checkPermission(childAccountPermissions, permissions.MANAGE_INSTANT_SURVEY_ASSESSMENTS)),
    [currentAccountPermissions, childAccountPermissions, isDistributionSponsor]
  );
};

/**
 * Strips the "Instant Created" option from the generated `status`
 * column's filter. Operates on useGenerateColumns output (the filter
 * config lives under `column.meta.qhsFilter`).
 */
export const hideInstantCreatedFilterOption = columns =>
  columns.map(column => {
    const qhsFilter = column.meta?.qhsFilter;
    if (column.accessorKey !== 'status' || !qhsFilter?.options) return column;
    return {
      ...column,
      meta: {
        ...column.meta,
        qhsFilter: {
          ...qhsFilter,
          options: qhsFilter.options.filter(o => o.value !== ASSESSMENT_STATUSES.created),
        },
      },
    };
  });
